import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../lib/api";
import { toast } from "sonner";
import LeadDrawer from "../components/LeadDrawer";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { statusMeta } from "../lib/constants";
import {
  Target, Zap, MessageSquare, PhoneCall, Repeat, Sparkles, Loader2, Inbox, CheckCircle2, Bot,
} from "lucide-react";

const ACTION = {
  reply: { label: "Reply now", icon: MessageSquare, cls: "text-emerald-400" },
  sms: { label: "Text them", icon: MessageSquare, cls: "text-sky-400" },
  followup: { label: "Follow up", icon: Repeat, cls: "text-amber-400" },
  call: { label: "Call to close", icon: PhoneCall, cls: "text-purple-400" },
  review: { label: "Review", icon: Sparkles, cls: "text-muted-foreground" },
};

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`brain-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className={`h-4 w-4 ${accent}`} />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="mt-3 font-display text-3xl font-extrabold tabular-nums">{value}</p>
    </div>
  );
}

function priorityColor(p) {
  if (p >= 70) return "#F43F5E";
  if (p >= 45) return "#F59E0B";
  return "#22D3EE";
}

export default function Attention() {
  const qc = useQueryClient();
  const [drawerId, setDrawerId] = useState(null);
  const [running, setRunning] = useState(false);

  const { data } = useQuery({
    queryKey: ["attention"],
    queryFn: () => api.get("/brain/attention").then((r) => r.data),
    refetchInterval: 30000,
  });
  const leads = data?.leads || [];
  const counts = data?.counts || {};
  const handsOff = data?.hands_off ?? true;

  const toggleHandsOff = async () => {
    try {
      await api.post("/brain/settings", { hands_off: !handsOff });
      qc.invalidateQueries({ queryKey: ["attention"] });
      toast.success(!handsOff ? "Autopilot ON — the AI will work stalled leads for you" : "Autopilot paused");
    } catch (e) {
      toast.error("Could not update autopilot");
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/brain/run");
      toast.success(`Brain ran — ${r.data.actions} action${r.data.actions === 1 ? "" : "s"} taken`);
      qc.invalidateQueries({ queryKey: ["attention"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
    } catch (e) {
      toast.error("Could not run the brain right now");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="attention-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Target className="h-7 w-7 text-primary" /> Needs Attention
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Your AI ranks who to work next and can run the outreach hands-off.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 rounded-full border border-border bg-card px-3 py-2" data-testid="handsoff-toggle">
            <Bot className={`h-4 w-4 ${handsOff ? "text-emerald-400" : "text-muted-foreground"}`} />
            <span className="text-sm">Autopilot</span>
            <Switch checked={handsOff} onCheckedChange={toggleHandsOff} data-testid="handsoff-switch" />
          </div>
          <Button onClick={runNow} disabled={running} data-testid="brain-run-button"
            className="bg-primary text-primary-foreground hover:bg-primary/90">
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Zap className="h-4 w-4 mr-1.5" />}
            Run now
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Target} label="Needs attention" value={counts.attention ?? 0} accent="text-primary" />
        <Stat icon={Inbox} label="Replied waiting" value={counts.replied_waiting ?? 0} accent="text-emerald-400" />
        <Stat icon={CheckCircle2} label="Qualified" value={counts.qualified ?? 0} accent="text-purple-400" />
        <Stat icon={Bot} label="On autopilot" value={counts.autopilot ?? 0} accent="text-sky-400" />
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="attention-list">
        {leads.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            Nothing needs attention right now. Import or work some leads and check back.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {leads.map((l) => {
              const a = ACTION[l.suggested_action] || ACTION.review;
              const sm = statusMeta(l.status);
              return (
                <button
                  key={l.id}
                  onClick={() => setDrawerId(l.id)}
                  data-testid={`attention-row-${l.id}`}
                  className="w-full text-left px-4 sm:px-5 py-4 flex items-center gap-4 hover:bg-secondary/40 transition-colors"
                >
                  <div className="flex flex-col items-center w-10 shrink-0">
                    <span className="font-display text-lg font-extrabold tabular-nums" style={{ color: priorityColor(l.priority) }}>
                      {l.priority}
                    </span>
                    <div className="h-1 w-8 rounded-full bg-secondary mt-1 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${l.priority}%`, background: priorityColor(l.priority) }} />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{l.name}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${sm.bg} ${sm.text}`}>{sm.label}</span>
                      {l.has_unread_reply && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">new reply</span>
                      )}
                      {l.in_autopilot && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400 flex items-center gap-1"><Bot className="h-3 w-3" />autopilot</span>
                      )}
                    </div>
                    <p className="text-sm text-primary/90 mt-1 truncate">
                      {l.ai_next_action || (l.reasons || []).join(" · ")}
                    </p>
                    {l.ai_reason && <p className="text-xs text-muted-foreground mt-0.5 truncate">{l.ai_reason}</p>}
                    {(l.category || l.city) && (
                      <p className="text-xs text-muted-foreground/70 mt-0.5 truncate">
                        {[l.category, l.city].filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </div>
                  <div className={`hidden sm:flex items-center gap-1.5 text-sm shrink-0 ${a.cls}`}>
                    <a.icon className="h-4 w-4" /> {a.label}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <LeadDrawer leadId={drawerId} open={!!drawerId} onOpenChange={(v) => !v && setDrawerId(null)} />
    </div>
  );
}
